import axios from "axios";
import AppConstants from "../Constants/AppConstants";
import { attachLoadingInterceptors } from "./loadingTracker";
const getAppConstants = new AppConstants().getAppConstants;
const http = axios.create({
    baseURL: getAppConstants.service.url,
    headers: {
        Accept: "application/json",
        "auth-token": getAppConstants.service.auth_token,
    },
    proxy: {
        port: 3006,
    },
    maxContentLength: 1024 * 1024 * 20,
});

// Unlike `http` (always localhost:3006), this points at REACT_APP_BACKEND_HOST in prod —
// the only endpoints meant to be reachable through the public nginx-proxied host.
const publicApiHttp = axios.create({
    baseURL: getAppConstants.service.publicUrl,
    headers: {
        Accept: "application/json",
        "auth-token": getAppConstants.service.auth_token,
    },
    maxContentLength: 1024 * 1024 * 20,
});

attachLoadingInterceptors(http);
attachLoadingInterceptors(publicApiHttp);

export const updateTrigger = async body => {
    const resp = await http.put(getAppConstants.service.gtt.endpoints.put.base, body);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const updateTriggerConfig = async body => {
    const resp = await http.put(getAppConstants.service.gtt.endpoints.put.config, body);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const getKiteToken = async () => {
    const resp = await http.get(getAppConstants.service.authorization.endpoints.get.token);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const updateKiteToken = async body => {
    const resp = await http.post(getAppConstants.service.authorization.endpoints.get.token, body);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const postCoinToken = async body => {
    const resp = await http.post(getAppConstants.service.authorization.endpoints.post.coin_token, body);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const postConsoleToken = async body => {
    const resp = await http.post(getAppConstants.service.authorization.endpoints.post.console_token, body);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const getHoldings = async () => {
    const resp = await http.get(getAppConstants.service.holdings.endpoints.get.all);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const getPublicPositions = async () => {
    const resp = await publicApiHttp.get(getAppConstants.service.positions.endpoints.get.public);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const getCompleteOrders = async () => {
    const resp = await publicApiHttp.get(getAppConstants.service.orders.endpoints.get.complete);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const getQueuedOrders = async () => {
    const resp = await publicApiHttp.get(getAppConstants.service.orders.endpoints.get.queue);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const getIndices = async () => {
    const resp = await publicApiHttp.get(getAppConstants.service.indices.endpoints.get.base);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const getTriggers = async () => {
    const resp = await publicApiHttp.get(getAppConstants.service.gtt.endpoints.get.public);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const getIndexObservers = async () => {
    const resp = await publicApiHttp.get(getAppConstants.service.indexObserver.endpoints.get.public);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const updateIndexObserver = async body => {
    const resp = await http.put(getAppConstants.service.indexObserver.endpoints.put.base, body);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const getMutualFundHoldings = async () => {
    const resp = await publicApiHttp.get(getAppConstants.service.mutualFundHoldings.endpoints.get.public);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const getTargets = async () => {
    const resp = await http.get(getAppConstants.service.targets.endpoints.get.all);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const updateTarget = async body => {
    const resp = await http.put(getAppConstants.service.targets.endpoints.put.base, body);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const addTarget = async body => {
    const resp = await http.post(getAppConstants.service.targets.endpoints.post.base, body);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const updateTriggerQuantity = async body => {
    const resp = await http.put(getAppConstants.service.gtt.endpoints.put.quantity, body);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const switchTriggerType = async body => {
    const resp = await http.put(getAppConstants.service.gtt.endpoints.put.switch_trigger_type, body);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

// Surfaces the backend's message (e.g. "XYZ is not a listed NSE / BSE stock or ETF") instead of axios' generic one.
const throwServerMessage = err => {
    throw new Error((err && err.response && err.response.data && err.response.data.message) || err.message);
};

export const getWatchlist = async () => {
    const resp = await publicApiHttp.get(getAppConstants.service.watchlist.endpoints.get.public);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const addWatchlist = async body => {
    const resp = await http.post(getAppConstants.service.watchlist.endpoints.post.base, body).catch(throwServerMessage);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const updateWatchlist = async body => {
    const resp = await http.put(getAppConstants.service.watchlist.endpoints.put.base, body).catch(throwServerMessage);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};

export const deleteWatchlist = async body => {
    const resp = await http
        .delete(getAppConstants.service.watchlist.endpoints.delete.base, { params: body })
        .catch(throwServerMessage);
    return resp.data ? (resp.data.resp ? resp.data.resp : resp.data) : resp;
};
